import api from './api.service';
import axios from 'axios';
import { LocalStorageService } from './local-storage.service';
import { SessionService } from './session.service';
import i18n from 'i18next';

const _HTTP_ = api;
const TWO_FACTOR_AUTH_PATHS = ['auth/2fa', 'auth/verify-2fa'];

const isTwoFactorAuthRequest = (url?: string): boolean => {
  const requestUrl = String(url || '');
  return TWO_FACTOR_AUTH_PATHS.some((path) => requestUrl.includes(path));
};

const isTwoFactorFlowScreen = (): boolean => {
  const pathname = String(window?.location?.pathname || '');
  return pathname.includes('/auth/2fa') || pathname.includes('/profile-settings/security');
};

const isAuthEndpoint = (url?: string): boolean =>
  /auth\/(login|refresh|logout|forgot-password|reset-password)/.test(String(url || ''));

_HTTP_.interceptors.response.use(
  async (response) => {
    return response.data;
  },
  async (error) => {
    if (axios.isCancel(error)) {
      return new Promise(() => {});
    }

    // Sin respuesta (red caída, CORS, timeout): no es un 401, no cerrar sesión.
    if (!error.response) {
      return Promise.reject({ status: error.message, error: i18n.t('errorCode.000') });
    }

    const httpStatus: number = error.response.status;
    const data = error.response.data ?? {};
    const { statusCode, message, errorCode } = data;
    const config = error.config || {};

    // 401 con access token vencido: refrescar (single-flight) y reintentar UNA vez la petición.
    // Si el refresh falla, SessionService ya limpió y mandó al login.
    if (
      httpStatus === 401 &&
      !config.__retried &&
      !isAuthEndpoint(config.url) &&
      !isTwoFactorAuthRequest(config.url)
    ) {
      const newToken = await SessionService.refreshAccessToken();
      if (newToken) {
        config.__retried = true;
        config.headers = { ...(config.headers || {}), Authorization: `Bearer ${newToken}` };
        return _HTTP_.request(config);
      }
      return new Promise(() => {}); // ya se redirigió al login; no propagar el error
    }

    return handleResponseCode(statusCode ?? httpStatus, message, errorCode, config.url);
  }
);

const handleResponseCode = (
  statusCode: number,
  errorMessage: string,
  errorCode: number,
  requestUrl?: string
) => {
  const t = i18n.t.bind(i18n);

  if (statusCode === 404) {
    return Promise.reject(t(errorCode ? `errorCode.${errorCode}` : 'errorCode.404'));
  }

  switch (errorCode || statusCode) {
    case 500:
      if (errorMessage.toLowerCase() === 'sesión caducada') {
        window.location.replace('/auth/login');
        LocalStorageService.clear();
        return;
      }
      if (errorMessage === 'Internal server error') {
        errorMessage = t('errors.error-500');
      }
      return Promise.reject({
        status: errorMessage,
        error: t(`errorCode.${errorCode || '000'}`)
      });
    case 401:
      if (isTwoFactorAuthRequest(requestUrl) || isTwoFactorFlowScreen()) {
        return Promise.reject({
          status: errorMessage,
          error: errorMessage || t(`errorCode.${errorCode || '000'}`)
        });
      }
      SessionService.expire('401 tras reintento');
      return new Promise(() => {});
    case 403:
      // El backend Raptor manda 403 con el motivo real en `message` (reglas de
      // jerarquia de usuarios, roles insuficientes...). Sin este case caia en el
      // default y el usuario veia "contacta al administrador" en lugar de, por
      // ejemplo, "Solo un superadmin puede borrar otro superadmin".
      return Promise.reject({
        status: errorMessage,
        error: errorMessage || t('errorCode.403')
      });
    case 409:
      return Promise.reject({
        status: errorMessage,
        error: t('errorCode.409')
      });
      break;
    case 400:
      return Promise.reject({
        status: errorMessage,
        error: errorMessage
      });
    default:
      return Promise.reject({
        status: errorMessage,
        error: t(`errorCode.${errorCode || '000'}`)
      });
  }
};

export const HTTP = _HTTP_;
